'use client'

import { PaymentErrorBanner } from '@/components/Pricing/PaymentErrorBanner'
import { UpgradeBanner } from '@/components/Pricing/UpgradeBanner'
import { UserProfileDropdown } from '@/components/UserProfile/UserProfileDropdown'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import Image from 'next/image'
import Link from 'next/link'

type HeaderProps = {
  actionButtons?: React.ReactNode
  showUpgradeBanner?: boolean
  showPaymentErrorBanner?: boolean
  upgradeOptions?: {
    checkExecuteCredits?: boolean
    checkAppCount?: boolean
    checkEditCredits?: boolean
  }
}

// Placeholder component that matches UserProfileDropdown styling
function UserProfilePlaceholder() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full">
      <div className="h-8 w-8 animate-pulse rounded-full bg-gray-400"></div>
    </div>
  )
}

export default function Header({
  actionButtons,
  showUpgradeBanner = true,
  showPaymentErrorBanner = true,
  upgradeOptions = {},
}: HeaderProps) {
  const { data: userData, isLoading: userLoading, error: userError } = useGetUserQuery()

  // Determine user state for rendering
  const renderUserProfile = () => {
    if (userLoading) {
      return <UserProfilePlaceholder />
    }

    if (userError) {
      return (
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-red-300 bg-red-100">
          <svg
            className="h-5 w-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      )
    }

    if (!userData) return <UserProfilePlaceholder />

    const user = userData.user
    const isAuthenticated = user && !user.isAnonymous

    return isAuthenticated ? <UserProfileDropdown /> : <UserProfilePlaceholder />
  }

  return (
    <>
      <header className="flex h-14 w-full items-center justify-between px-4 py-2">
        <div className="flex items-center">
          <Link href="/apps">
            <Image
              alt="Sprites logo"
              src="/sprites.svg"
              width={50}
              height={50}
              className="h-10 w-10 cursor-pointer hover:opacity-80"
            />
          </Link>
          <Link
            href="/apps"
            className="!font-libre-baskerville py-[6px] pl-[6px] text-[18px] font-normal md:text-[20px]"
          >
            Sprites AI
          </Link>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {actionButtons}
          {renderUserProfile()}
        </div>
      </header>
      {showUpgradeBanner && <UpgradeBanner {...upgradeOptions} />}
      {showPaymentErrorBanner && <PaymentErrorBanner />}
    </>
  )
}
